import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../shared/api/apiClient';
import type { UserDto, CreateUserRequest, UpdateUserRequest, User } from '../../types';
import { RefreshIcon } from '../../shared/icons';
import { ROLES, hasRole } from '../../utils/accessControl';
import './styles.css';

interface RoleOption {
    value: string;
    label: string;
    color: string;
}

const PRIMARY_ROLE_OPTIONS: RoleOption[] = [
    { value: ROLES.admin, label: 'Администратор', color: 'danger' },
    { value: ROLES.user, label: 'Пользователь', color: 'primary' }
];
const PRIMARY_ROLE_VALUES = PRIMARY_ROLE_OPTIONS.map(role => role.value);
const FEATURE_ROLE_OPTIONS: RoleOption[] = [
    { value: ROLES.attributeManager, label: 'Атрибуты', color: 'danger' },
    { value: ROLES.exclusionsEditor, label: 'Исключения', color: 'secondary' },
    { value: ROLES.diagnosticsViewer, label: 'Диагностика', color: 'success' },
    { value: ROLES.integrationsAccess, label: 'Интеграции', color: 'primary' },
    { value: ROLES.categoryEditor, label: 'Редактирование категорий', color: 'warning' },
    { value: ROLES.b2bClientsManager, label: 'B2B клиенты', color: 'info' },
    { value: ROLES.shopCompaniesManager, label: 'Компании магазина', color: 'success' },
    { value: ROLES.shopOrdersManager, label: 'Заказы магазина', color: 'secondary' }
];
const FEATURE_ROLE_VALUES = FEATURE_ROLE_OPTIONS.map(role => role.value);

const normalizeEditableRoles = (roles: string[] = []): string[] => {
    const primaryRole = PRIMARY_ROLE_VALUES.find(role => roles.includes(role));
    const normalizedRoles = primaryRole ? [primaryRole] : [];

    normalizedRoles.push(...FEATURE_ROLE_VALUES.filter(role => roles.includes(role)));

    return normalizedRoles;
};

const getRoleLabel = (role: string): string => {
    switch (role) {
        case ROLES.admin:
            return 'Админ';
        case ROLES.user:
            return 'Пользователь';
        case ROLES.service:
            return 'Сервис';
        case ROLES.attributeManager:
            return 'Атрибуты';
        case ROLES.exclusionsEditor:
            return 'Исключения';
        case ROLES.diagnosticsViewer:
            return 'Диагностика';
        case ROLES.integrationsAccess:
            return 'Интеграции';
        case ROLES.categoryEditor:
            return 'Категории';
        case ROLES.b2bClientsManager:
            return 'B2B';
        case ROLES.shopCompaniesManager:
            return 'Компании';
        case ROLES.shopOrdersManager:
            return 'Заказы';
        default:
            return role;
    }
};

const getRoleBadgeClass = (role: string): string => {
    switch (role) {
        case ROLES.admin:
            return 'bg-danger';
        case ROLES.service:
            return 'bg-info';
        case ROLES.attributeManager:
            return 'bg-danger';
        case ROLES.exclusionsEditor:
            return 'bg-secondary';
        case ROLES.diagnosticsViewer:
            return 'bg-success';
        case ROLES.integrationsAccess:
            return 'bg-primary';
        case ROLES.categoryEditor:
            return 'bg-warning';
        case ROLES.b2bClientsManager:
            return 'bg-info';
        case ROLES.shopCompaniesManager:
            return 'bg-success';
        case ROLES.shopOrdersManager:
            return 'bg-secondary';
        default:
            return 'bg-secondary';
    }
};

// Universal User Modal Component (Create & Edit)
const UserModal: React.FC<{
    user?: UserDto; // Optional for create mode
    onSave: (data: CreateUserRequest | UpdateUserRequest) => Promise<void>;
    onClose: () => void;
    isOpen: boolean;
    mode: 'create' | 'edit';
    currentUser: User | null;
    isEditingSelf?: boolean;
}> = ({ user, onSave, onClose, isOpen, mode, currentUser, isEditingSelf = false }) => {
    const [loading, setLoading] = useState(false);

    // Initialize form data based on mode
    const getInitialFormData = React.useCallback(() => {
        if (mode === 'create') {
            return {
                userName: '',
                email: '',
                roles: [] as string[]
            } as CreateUserRequest;
        } else {
            return {
                userName: user?.userName || '',
                email: user?.email || '',
                roles: normalizeEditableRoles(user?.roles || []),
            } as UpdateUserRequest;
        }
    }, [mode, user]);

    const [formData, setFormData] = useState<CreateUserRequest | UpdateUserRequest>(getInitialFormData());

    useEffect(() => {
        setFormData(getInitialFormData());
    }, [getInitialFormData]);

    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    const handleSave = async () => {
        if (!formData.userName?.trim() || !formData.email?.trim()) return;

        // Check if password is required for create mode
        if (mode === 'create' && !formData.password?.trim()) return;

        setLoading(true);
        try {
            await onSave(formData);
            onClose();
        } catch (error) {
            console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} user:`, error);
        } finally {
            setLoading(false);
        }
    };

    const handlePrimaryRoleChange = (role: string) => {
        setFormData(prev => {
            const additionalRoles = (prev.roles || []).filter(r => !PRIMARY_ROLE_VALUES.includes(r));

            return {
                ...prev,
                roles: [role, ...additionalRoles]
            };
        });
    };

    const handleFeatureRoleChange = (role: string, checked: boolean) => {
        setFormData(prev => {
            const roles = prev.roles || [];
            const updatedRoles = checked
                ? [...roles.filter(existingRole => existingRole !== role), role]
                : roles.filter(existingRole => existingRole !== role);

            return {
                ...prev,
                roles: updatedRoles
            };
        });
    };

    if (!isOpen) return null;

    const isCreateMode = mode === 'create';
    const title = isCreateMode ? 'Создание пользователя' : 'Редактирование пользователя';
    const subtitle = isCreateMode ? 'Добавление нового пользователя в систему' : 'Управление профилем и правами доступа';
    const saveButtonText = isCreateMode ? 'Создать пользователя' : 'Сохранить изменения';
    const passwordPlaceholder = isCreateMode ? 'Введите пароль' : 'Оставьте пустым, чтобы сохранить текущий пароль';
    const passwordRequired = isCreateMode && !formData.password?.trim();
    const selectedPrimaryRole = PRIMARY_ROLE_VALUES.find(role => formData.roles?.includes(role)) || '';
    const primaryRoleRequired = !selectedPrimaryRole;
    const availablePrimaryRoles = hasRole(currentUser, ROLES.admin)
        ? PRIMARY_ROLE_OPTIONS
        : PRIMARY_ROLE_OPTIONS.filter(role => role.value !== ROLES.admin);

    return (
        <div className="modal-backdrop-custom" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div className="d-flex align-items-center">
                            <div className="user-avatar mr-3">
                                {formData.userName?.charAt(0).toUpperCase() || (isCreateMode ? '+' : 'U')}
                            </div>
                            <div>
                                <h3 className="modal-title-custom">{title}</h3>
                                <p className="modal-subtitle-custom">{subtitle}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close-custom"
                            onClick={onClose}
                            disabled={loading}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <div className="modal-body-custom">
                        <div className="form-section">
                            <div className="form-grid">
                                <div className="form-group-custom">
                                    <label className="form-label-custom">
                                        <span className="label-icon">👤</span>
                                        Имя пользователя
                                    </label>
                                    <div className="input-group-custom">
                                        <input
                                            type="text"
                                            className="form-input-custom"
                                            value={formData.userName || ''}
                                            onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                                            placeholder="Введите имя пользователя"
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <div className="form-group-custom">
                                    <label className="form-label-custom">
                                        <span className="label-icon">📧</span>
                                        Email адрес
                                    </label>
                                    <div className="input-group-custom">
                                        <input
                                            type="email"
                                            className="form-input-custom"
                                            value={formData.email || ''}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="user@example.com"
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <div className="form-group-custom">
                                    <label className="form-label-custom">
                                        <span className="label-icon">🔑</span>
                                        Пароль {isCreateMode && <span className="text-danger">*</span>}
                                    </label>
                                    <div className="input-group-custom">
                                        <input
                                            type="password"
                                            className="form-input-custom"
                                            value={('password' in formData) ? formData.password || '' : ''}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            placeholder={passwordPlaceholder}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h4 className="section-title">
                                    Роли и права доступа
                                </h4>
                                <div className="roles-grid-simple">
                                    {availablePrimaryRoles.map(role => (
                                        <div key={role.value} className={`role-option ${selectedPrimaryRole === role.value ? `selected ${role.color}` : ''}`}>
                                            <div className="form-check-custom">
                                                <input
                                                    type="radio"
                                                    className="form-check-input-custom"
                                                    id={`${mode}-${role.value}`}
                                                    name={`${mode}-primary-role`}
                                                    checked={selectedPrimaryRole === role.value}
                                                    onChange={() => handlePrimaryRoleChange(role.value)}
                                                    disabled={loading || isEditingSelf}
                                                />
                                                <label className="form-check-label-custom" htmlFor={`${mode}-${role.value}`}>
                                                    {role.label}
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="roles-grid-simple mt-3">
                                    {FEATURE_ROLE_OPTIONS.map(role => {
                                        const hasFeatureRole = formData.roles?.includes(role.value) || false;

                                        return (
                                            <div key={role.value} className={`role-option ${hasFeatureRole ? `selected ${role.color}` : ''}`}>
                                                <div className="form-check-custom">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input-custom"
                                                        id={`${mode}-${role.value}`}
                                                        checked={hasFeatureRole}
                                                        onChange={(e) => handleFeatureRoleChange(role.value, e.target.checked)}
                                                        disabled={loading || isEditingSelf}
                                                    />
                                                    <label className="form-check-label-custom" htmlFor={`${mode}-${role.value}`}>
                                                        {role.label}
                                                    </label>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer-custom">
                        <button
                            type="button"
                            className="btn-secondary-custom"
                            onClick={onClose}
                            disabled={loading}
                        >
                            <span className="btn-icon">✕</span>
                            Отмена
                        </button>
                        <button
                            type="button"
                            className="btn-primary-custom"
                            onClick={handleSave}
                            disabled={loading || !formData.userName?.trim() || !formData.email?.trim() || passwordRequired || primaryRoleRequired}
                        >
                            {loading ? (
                                <>
                                    <span className="btn-spinner"></span>
                                    {isCreateMode ? 'Создание...' : 'Сохранение...'}
                                </>
                            ) : (
                                <>
                                    <span className="btn-icon">✓</span>
                                    {saveButtonText}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// User Table Row Component
const UserTableRow: React.FC<{
    user: UserDto;
    onEdit: (user: UserDto) => void;
    onDelete: (id: string) => Promise<void>;
    currentUser: User | null;
}> = ({ user, onEdit, onDelete, currentUser }) => {
    const handleDelete = async () => {
        if (confirm(`Вы действительно хотите навсегда удалить пользователя "${user.userName}"?\n\nЭто действие нельзя отменить!`)) {
            await onDelete(user.id);
        }
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('ru-RU');
    };

    // Check if this user is the current user
    const isCurrentUser = currentUser && String(user.id) === String(currentUser.id);

    // Проверяем, можно ли редактировать пользователя
    const canEdit = !user.roles.includes(ROLES.service);
    const canDelete = !user.roles.includes(ROLES.service) && !isCurrentUser;

    return (
        <tr>
            <td>{user.userName}</td>
            <td>{user.email || '-'}</td>
            <td>
                <div className="user-role-list">
                    {(user.roles || []).map((role, index) => (
                        <React.Fragment key={role}>
                            {index > 0 && <span className="user-role-separator">/</span>}
                            <span className={`badge ${getRoleBadgeClass(role)}`}>
                                {getRoleLabel(role)}
                            </span>
                        </React.Fragment>
                    ))}
                </div>
                {(user.roles || []).length === 0 && (
                    <small className="text-muted">Нет ролей</small>
                )}
            </td>
            <td>{formatDate(user.createdAt)}</td>
            <td>{formatDate(user.lastLogin)}</td>
            <td className="actions-column">
                <div className="btn-group btn-group-sm">
                    {canEdit &&
                        <button
                            className="btn btn-small btn-edit"
                            onClick={() => onEdit(user)}
                            title={canEdit ? "Редактировать пользователя" : "Нельзя редактировать сервисного пользователя"}
                            disabled={!canEdit}
                        >
                            ✏️
                        </button>
                    }
                    {canDelete &&
                        <button
                            className="btn btn-small btn-delete"
                            onClick={handleDelete}
                            title={canDelete ? "Полностью удалить пользователя" : "Нельзя удалить сервисного пользователя"}
                            disabled={!canDelete}
                        >
                            ✕
                        </button>
                    }
                </div>
            </td>
        </tr>
    );
};

const UserManagement: React.FC<{ currentUser: User | null }> = ({ currentUser }) => {
    const location = useLocation();
    const [users, setUsers] = useState<UserDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRole, setSelectedRole] = useState<string>('');
    const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
    const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);

    const isAdmin = hasRole(currentUser, ROLES.admin);

    useEffect(() => {
        loadUsers();
    }, [location.search, isAdmin]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);

            const apiUrl = '/api/admin/users';
            const result = await api.get<{ users: UserDto[] }>(apiUrl);
            setUsers(result.data.users || []);
        } catch (err) {
            setError('Произошла ошибка при загрузке пользователей');
            console.error('Error loading users:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = !searchTerm ||
            user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRole = !selectedRole || user.roles.includes(selectedRole);

        return matchesSearch && matchesRole;
    });

    // Universal save handler for both create and edit modes
    const handleSaveUser = async (data: CreateUserRequest | UpdateUserRequest) => {
        const isCreateMode = modalMode === 'create';

        const createUrl = '/api/admin/users';
        const updateUrl = `/api/admin/users/${selectedUser?.id}`;

        if (isCreateMode) {
            // This is a CreateUserRequest - POST request
            await api.post(createUrl, data);
            toast.success('Пользователь создан успешно!');
            loadUsers();
            setModalMode(null);
            setSelectedUser(null);
        } else {
            // This is an UpdateUserRequest - PUT request
            const updateData = data as UpdateUserRequest;
            await api.put(updateUrl, updateData);
            toast.success('Пользователь обновлен успешно!');
            loadUsers();
            setModalMode(null);
            setSelectedUser(null);
        }
    };

    // Handlers for opening modals
    const handleCreateClick = () => {
        setModalMode('create');
        setSelectedUser(null);
    };

    const handleEditClick = (user: UserDto) => {
        setModalMode('edit');
        setSelectedUser(user);
    };

    const handleModalClose = () => {
        setModalMode(null);
        setSelectedUser(null);
    };



    const handleDeleteUser = async (id: string) => {
        // Note: Since DELETE removes user permanently, we should use this carefully
        const deleteUrl = `/api/admin/users/${id}`;
        await api.delete(deleteUrl);
        toast.success('Пользователь удален навсегда!');
        loadUsers();
    };

    if (error) {
        return (
            <div className="alert alert-danger">
                <h5 className="alert-heading">Ошибка</h5>
                <p>{error}</p>
                <button className="btn btn-primary" onClick={loadUsers}>
                    Повторить загрузку
                </button>
            </div>
        );
    }

    return (
        <div className="user-management container-fluid">
            <div className="filter-controls-compact">
                <div className="filter-controls-row">
                    <h3>Пользователи</h3>
                    
                    <div className="filter-controls-right">
                        <div className="role-filter-compact">
                            <select
                                className="form-select-compact"
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value)}
                            >
                                <option value="">Все роли</option>
                                <option value={ROLES.admin}>Админ</option>
                                <option value={ROLES.user}>Пользователь</option>
                                {FEATURE_ROLE_OPTIONS.map(role => (
                                    <option key={role.value} value={role.value}>{role.label}</option>
                                ))}
                                <option value={ROLES.service}>Сервис</option>
                            </select>
                        </div>
                        
                        <div className="search-input-compact">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="search-icon">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input
                                type="text"
                                className="form-control-compact"
                                placeholder="Поиск..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    className="clear-search"
                                    onClick={() => setSearchTerm('')}
                                    title="Очистить поиск"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            )}
                        </div>

                        <button className="btn-compact refresh" onClick={loadUsers} disabled={loading} title="Обновить">
                            {loading ? (
                                <span className="btn-spinner"></span>
                            ) : (
                                <RefreshIcon />
                            )}
                        </button>

                        <button className="btn-compact primary" onClick={handleCreateClick}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14"/>
                            </svg>
                            <span>Создать</span>
                        </button>
                    </div>
                </div>

                {/* Active Filters Tags */}
                {(searchTerm || selectedRole) && (
                    <div className="active-filters-compact">
                        {searchTerm && (
                            <span className="filter-tag-compact">
                                <span className="tag-text">Поиск: "{searchTerm}"</span>
                                <button
                                    className="tag-remove-compact"
                                    onClick={() => setSearchTerm('')}
                                    title="Удалить фильтр"
                                >
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            </span>
                        )}
                        {selectedRole && (
                            <span className="filter-tag-compact">
                                <span className="tag-text">
                                    Роль: {getRoleLabel(selectedRole)}
                                </span>
                                <button
                                    className="tag-remove-compact"
                                    onClick={() => setSelectedRole('')}
                                    title="Удалить фильтр"
                                >
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Users Table */}
            <div className="user-management-table-container">
                <table className="user-management-table">
                    <thead>
                        <tr>
                            <th>Имя</th>
                            <th>Email</th>
                            <th>Роли</th>
                            <th>Создан</th>
                            <th>Последний вход</th>
                            <th className="actions-column"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map(user => (
                            <UserTableRow
                                key={user.id}
                                user={user}
                                onEdit={handleEditClick}
                                onDelete={handleDeleteUser}
                                currentUser={currentUser}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredUsers.length === 0 && (
                <div className="text-center py-4">
                    <p className="text-muted">Пользователи не найдены</p>
                </div>
            )}

            <div className="mt-3 text-muted">
                Показано {filteredUsers.length} из {users.length} пользователей
            </div>

            {/* Universal User Modal */}
            <UserModal
                user={selectedUser || undefined}
                onSave={handleSaveUser}
                onClose={handleModalClose}
                isOpen={modalMode !== null}
                mode={modalMode || 'create'}
                currentUser={currentUser}
                isEditingSelf={!!(selectedUser && currentUser && String(selectedUser.id) === String(currentUser.id))}
            />
        </div>
    );
};

export default UserManagement;
